import React, { useEffect, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  Calendar,
  ChevronDown,
  Filter,
  Search,
  ShieldAlert,
  X,
} from 'lucide-react';
import AccordionPanel from '../../../components/AccordionPanel';

const CustomDateInput = React.forwardRef(({ value, onClick, placeholder }, ref) => (
  <div className="custom-date-input-wrapper" onClick={onClick} ref={ref}>
    <input
      type="text"
      readOnly
      value={value}
      placeholder={placeholder || 'MM-DD-YYYY'}
      className="custom-date-input-field"
    />
  </div>
));

export const AnswersRagScopeChips = ({ chips }) => (
  <div className="answers-scope-chips" aria-label="Current answer scope">
    {chips.length ? chips.map((chip) => (
      <span className="answers-scope-chip" key={`${chip.label}-${chip.count}`}>
        <span className="answers-scope-chip-label">{chip.label}</span>
        <strong className="answers-scope-chip-count" aria-label={`${chip.count} selected`}>
          ({chip.count})
        </strong>
      </span>
    )) : (
      <span className="answers-scope-chip answers-scope-chip--default">
        <span className="answers-scope-chip-value">All filings</span>
      </span>
    )}
  </div>
);

export const AnswersRagFilterPanel = ({
  children,
  drawerFiltersMatchText,
  isOpen,
  onApplyFilters,
  onClearDraftFilters,
  onClose,
}) => {
  const handleClearDraft = onClearDraftFilters;

  return (
  <>
    <div className={`drawer-overlay ${isOpen ? 'open' : ''}`} onClick={onClose} />
    <div className={`details-drawer filters-details-drawer ${isOpen ? 'open' : ''}`} aria-hidden={!isOpen}>
      <div className="drawer-header filters-drawer-header">
        <div className="filters-drawer-header-copy">
          <h3 className="drawer-title filters-drawer-title-wrap">
            <Filter size={18} />
            <span>Filters</span>
          </h3>
          {drawerFiltersMatchText && (
            <p className="filters-drawer-header-sub" aria-live="polite">{drawerFiltersMatchText}</p>
          )}
        </div>
        <button type="button" className="drawer-close filters-drawer-close" onClick={onClose} aria-label="Close filters">
          ×
        </button>
      </div>
      <div className="drawer-body filters-drawer-body">
        {children}
      </div>
      <div className="drawer-footer filters-drawer-footer">
        <button type="button" className="btn btn-sm filters-drawer-btn-reset" onClick={handleClearDraft}>
          Reset All
        </button>
        <button type="button" className="btn btn-sm filters-drawer-btn-apply" onClick={onApplyFilters}>
          Apply Filters
        </button>
      </div>
    </div>
  </>
  );
};

export const AnswersRagAccordionFilter = ({
  domId,
  title,
  Icon,
  options = [],
  value = [],
  onChange,
  optionLabel = (x) => x,
  optionValue = (x) => x,
  searchPlaceholder = 'Search',
  isOpen,
  onToggle,
}) => {
  const [search, setSearch] = useState('');
  const selected = value || [];
  const filtered = options.filter((item) => optionLabel(item).toLowerCase().includes(search.toLowerCase()));
  const allValues = options.map((item) => optionValue(item));

  const toggleValue = (item) => {
    const v = optionValue(item);
    onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
  };

  const handleSelectAll = () => {
    if (selected.length === allValues.length) {
      onChange([]);
    } else {
      onChange([...allValues]);
    }
  };

  const removeValue = (v) => {
    onChange(selected.filter((x) => x !== v));
  };

  const labelForValue = (v) => {
    const match = options.find((o) => optionValue(o) === v);
    return match != null ? optionLabel(match) : v;
  };

  return (
    <div className="accordion-item" id={domId}>
      <div
        className="accordion-header"
        onClick={() => {
          onToggle();
          setSearch('');
        }}
      >
        <div className="accordion-header-left">
          <Icon size={18} className="accordion-header-icon" />
          <span className="accordion-header-title">{title}</span>
        </div>
        <div className="accordion-header-right">
          {selected.length > 0 && (
            <span key={selected.length} className="accordion-selected-count">
              {selected.length}
            </span>
          )}
          <ChevronDown size={16} className={`accordion-chevron ${isOpen ? 'is-open' : ''}`} />
        </div>
      </div>

      <AccordionPanel isOpen={isOpen}>
          <div className="custom-dropdown-header accordion-search-container">
            <div className="custom-dropdown-search-wrap">
              <Search size={16} className="custom-dropdown-search-icon" />
              <input
                type="text"
                className="custom-dropdown-search-input accordion-search-input"
                placeholder={searchPlaceholder}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
          <div className="custom-dropdown-item select-all" onClick={handleSelectAll}>
            <input
              type="checkbox"
              className="custom-dropdown-checkbox"
              checked={selected.length > 0 && selected.length === allValues.length}
              readOnly
            />
            <span>Select all</span>
          </div>
          <div className="custom-dropdown-list filters-popover-list accordion-dropdown-list">
            {filtered.map((item) => {
              const v = optionValue(item);
              const isChecked = selected.includes(v);
              return (
                <div key={v} className="custom-dropdown-item" onClick={() => toggleValue(item)}>
                  <input type="checkbox" className="custom-dropdown-checkbox" checked={isChecked} readOnly />
                  <span>{optionLabel(item)}</span>
                </div>
              );
            })}
          </div>
          {selected.length > 0 && (
            <div className="filters-badge-container">
              {selected.map((v) => (
                <div key={v} className="filters-badge">
                  <span>{labelForValue(v)}</span>
                  <button type="button" onClick={() => removeValue(v)} className="filters-badge-cancel">
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}
      </AccordionPanel>
    </div>
  );
};

export const AnswersRagObjectionsFilter = ({ domId, value, onChange, isOpen, onToggle }) => {
  const options = [
    { label: 'All', value: '' },
    { label: 'Yes', value: 'true' },
    { label: 'No', value: 'false' },
  ];
  const count = value === 'true' || value === 'false' ? 1 : 0;

  return (
    <div className="accordion-item" id={domId}>
      <div className="accordion-header" onClick={onToggle}>
        <div className="accordion-header-left">
          <ShieldAlert size={18} className="accordion-header-icon" />
          <span className="accordion-header-title">Objections</span>
        </div>
        <div className="accordion-header-right">
          {count > 0 && <span className="accordion-selected-count">{count}</span>}
          <ChevronDown size={16} className={`accordion-chevron ${isOpen ? 'is-open' : ''}`} />
        </div>
      </div>
      <AccordionPanel isOpen={isOpen}>
        <div className="accordion-content-divider" aria-hidden="true" />
        <div className="custom-dropdown-item select-all" onClick={() => onChange('')}>
          <input
            type="checkbox"
            className="custom-dropdown-checkbox"
            checked={value === ''}
            readOnly
          />
          <span>All</span>
        </div>
        <div className="custom-dropdown-list filters-popover-list accordion-dropdown-list">
          {options
            .filter((opt) => opt.value !== '')
            .map((opt) => (
              <div
                key={opt.label}
                className="custom-dropdown-item"
                onClick={() => onChange(opt.value)}
              >
                <input
                  type="checkbox"
                  className="custom-dropdown-checkbox"
                  checked={value === opt.value}
                  readOnly
                />
                <span>{opt.label}</span>
              </div>
            ))}
        </div>
      </AccordionPanel>
    </div>
  );
};

const parseDate = (value) => {
  if (!value) return null;
  const d = new Date(`${value}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
};

const formatDate = (date) => {
  if (!date) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

export const AnswersRagDateRangeFilter = ({
  domId,
  title,
  startDate,
  endDate,
  onChange,
}) => {
  const datePickerRef = useRef(null);
  const [tempRange, setTempRange] = useState([parseDate(startDate), parseDate(endDate)]);
  const count = startDate && endDate ? 1 : 0;

  useEffect(() => {
    setTempRange([parseDate(startDate), parseDate(endDate)]);
  }, [startDate, endDate]);

  return (
    <div className="accordion-item filters-date-range-item" id={domId}>
      <div className="accordion-header filters-date-range-header">
        <div className="accordion-header-left">
          <Calendar size={18} className="accordion-header-icon" />
          <span className="accordion-header-title">{title}</span>
        </div>
        <div className="accordion-header-right filters-date-range-controls">
          {count > 0 && <span className="accordion-selected-count">{count}</span>}
          <div className="filters-date-range-picker">
            <DatePicker
              selectsRange
              startDate={tempRange[0]}
              endDate={tempRange[1]}
              onChange={(update) => {
                setTempRange(update || [null, null]);
                if (!update || (!update[0] && !update[1])) {
                  onChange({ start: '', end: '' });
                }
              }}
              onCalendarOpen={() => setTempRange([parseDate(startDate), parseDate(endDate)])}
              minDate={new Date('1951-03-31')}
              maxDate={new Date()}
              isClearable
              placeholderText="MM/DD/YYYY"
              dateFormat="MM/dd/yyyy"
              showMonthDropdown
              showYearDropdown
              scrollableYearDropdown
              yearDropdownItemNumber={80}
              dropdownMode="scroll"
              popperPlacement="bottom-end"
              shouldCloseOnSelect={false}
              ref={datePickerRef}
              customInput={<CustomDateInput />}
            >
              <div className="calendar-confirm-bar">
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  onClick={() => {
                    onChange({
                      start: formatDate(tempRange[0]),
                      end: formatDate(tempRange[1]),
                    });
                    datePickerRef.current?.setOpen(false);
                  }}
                  disabled={!tempRange[0]}
                >
                  Apply Range
                </button>
              </div>
            </DatePicker>
          </div>
        </div>
      </div>
    </div>
  );
};
